/**
 * FaxReviewStep
 *
 * Final wizard step that displays a read-only summary of the fax configuration
 * before sending. It validates that a recipient number exists and that at least
 * one attachment (blob or document instance) has been selected. When the step is
 * valid, it signals completion to the parent wizard via `markStepComplete`.
 *
 * This step fetches blob and document metadata to present friendly names for
 * selected attachments and formats the recipient phone number for display.
 */
import { DocumentIcon, PhoneIcon } from '@heroicons/react/24/outline';
import React from 'react';

import { WizardStep } from '@/components/postgrid/wizard/WizardStep';
import { useGetBlobsQuery } from '@/services/api/blobService';
import { useGetMatterDocumentsQuery } from '@/services/api/mattersService';

/**
 * Wizard data used to render the review summary.
 */
interface WizardData {
  recipient_number?: string;
  attachment_blob_ids?: string[];
  attachment_document_ids?: string[];
  cover_page_to?: string;
  cover_page_from?: string;
  cover_page_message?: string;
  [key: string]: unknown;
}

/**
 * Props for FaxReviewStep.
 *
 * - `matterId`: The current matter id for fetching blobs and documents.
 * - `wizardData`: Aggregated wizard data to summarize.
 * - `markStepComplete`: Callback to communicate validity to the parent wizard.
 * - `className`: Optional className for the step container.
 */
interface FaxReviewStepProps {
  matterId: string;
  wizardData?: WizardData;
  markStepComplete?: (isValid: boolean) => void;
  className?: string;
}

/**
 * Formats a US phone number for display, with best-effort handling of +1 prefix.
 */
const formatPhoneNumber = (phoneNumber: string) => {
  const cleaned = phoneNumber.replace(/\D/g, '');
  if (cleaned.length === 11 && cleaned.startsWith('1')) {
    return `+1 (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
  }
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  }
  return phoneNumber;
};

/**
 * Displays a read-only review of recipient and attachment selections, and
 * communicates step validity to the parent wizard.
 */
export const FaxReviewStep: React.FC<FaxReviewStepProps> = ({
  matterId,
  wizardData = {},
  markStepComplete,
  className = '',
}) => {
  const { data: blobs } = useGetBlobsQuery(matterId);
  const { data: documents = [] } = useGetMatterDocumentsQuery(matterId);

  const selectedBlobs = React.useMemo(() => {
    if (!blobs || !wizardData.attachment_blob_ids) return [];
    return blobs.filter((blob) =>
      wizardData.attachment_blob_ids?.includes(blob.id),
    );
  }, [blobs, wizardData.attachment_blob_ids]);

  const selectedDocuments = React.useMemo(() => {
    if (!documents || !wizardData.attachment_document_ids) return [];
    return (documents as any[]).filter((d) =>
      wizardData.attachment_document_ids?.includes(d.id),
    );
  }, [documents, wizardData.attachment_document_ids]);

  const recipientNumber = wizardData.recipient_number || '';
  const hasAttachments =
    (wizardData.attachment_blob_ids?.length || 0) > 0 ||
    (wizardData.attachment_document_ids?.length || 0) > 0;
  const isValid = recipientNumber.trim() !== '' && hasAttachments;
  const coverTo = (wizardData.cover_page_to as string) || '';
  const coverFrom = (wizardData.cover_page_from as string) || '';
  const coverMessage = (wizardData.cover_page_message as string) || '';

  React.useEffect(() => {
    markStepComplete?.(isValid);
  }, [isValid, markStepComplete]);

  return (
    <WizardStep
      title="Review & Send"
      description="Review your fax details before sending."
      isValid={isValid}
      markStepComplete={markStepComplete}
      className={className}
    >
      <div className="space-y-6">
        {/* Recipient Information */}
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="flex items-center gap-3 mb-3">
            <PhoneIcon className="h-5 w-5 text-gray-600" />
            <h3 className="font-medium text-gray-900">Recipient</h3>
          </div>
          <div className="text-sm text-gray-700">
            <strong>Fax Number:</strong> {formatPhoneNumber(recipientNumber)}
          </div>
          {(coverTo || coverFrom || coverMessage) && (
            <div className="mt-3 text-sm text-gray-700">
              <div className="font-medium text-gray-900 mb-1">Cover Sheet</div>
              {coverTo && (
                <div>
                  <strong>To:</strong> {coverTo}
                </div>
              )}
              {coverFrom && (
                <div>
                  <strong>From:</strong> {coverFrom}
                </div>
              )}
              {coverMessage && (
                <div>
                  <strong>Message:</strong> {coverMessage}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Attachments */}
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="flex items-center gap-3 mb-3">
            <DocumentIcon className="h-5 w-5 text-gray-600" />
            <h3 className="font-medium text-gray-900">Documents</h3>
          </div>

          {selectedBlobs.length + selectedDocuments.length > 0 ? (
            <div className="space-y-2">
              {selectedDocuments.map((doc: any) => (
                <div
                  key={doc.id}
                  className="flex items-center gap-2 text-sm text-gray-700 bg-white rounded p-2"
                >
                  <DocumentIcon className="h-4 w-4 text-gray-500" />
                  <span className="flex-1 truncate">
                    {doc.document_template?.name || 'Document'}
                  </span>
                  <span className="text-xs text-gray-500 uppercase">LOR</span>
                </div>
              ))}
              {selectedBlobs.map((blob) => (
                <div
                  key={blob.id}
                  className="flex items-center gap-2 text-sm text-gray-700 bg-white rounded p-2"
                >
                  <DocumentIcon className="h-4 w-4 text-gray-500" />
                  <span className="flex-1 truncate">
                    {blob.original_filename || blob.name}
                  </span>
                  <span className="text-xs text-gray-500 uppercase">
                    {blob.file_extension?.replace('.', '') || 'PDF'}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-sm text-red-600">
              No documents selected. Please go back and select at least one
              document.
            </div>
          )}
        </div>

        {/* Summary */}
        <div className="border-t border-gray-200 pt-4">
          <div className="text-sm text-gray-600">
            <p>
              <strong>{selectedBlobs.length + selectedDocuments.length}</strong>{' '}
              document
              {selectedBlobs.length + selectedDocuments.length !== 1
                ? 's'
                : ''}{' '}
              will be sent to{' '}
              <strong>{formatPhoneNumber(recipientNumber)}</strong>
            </p>
            <p className="mt-2 text-xs">
              You&apos;ll receive updates about the fax status after sending.
            </p>
          </div>
        </div>
      </div>
    </WizardStep>
  );
};
